<template>
<div>
    <section class="section_logo-bar">
        <div class="logonav_wrap">
            <div class="logonav_div">
                <a href="#" class="link-block-92 w-inline-block">
                    <img sizes="(max-width: 1030px) 100vw, 1030px" 
                         srcset="@/assets/images/PriMeds_LogoNEW-p-500.png 500w, @/assets/images/PriMeds_LogoNEW-p-800.png 800w, @/assets/images/PriMeds_LogoNEW.png 1030w" 
                         alt="" 
                         src="@/assets/images/PriMeds_LogoNEW.png" 
                         loading="lazy" 
                         class="image-583">
                </a>
            </div>
        </div>
    </section>
    
    <section>
        <div class="upload-wrap">
            <div class="uploadvid_div">
                <div class="text-block-1513">
                    A Quick Video is Required<br>
                    <span class="text-span-341">(Per {{ getUserState }} Regulations)</span>
                </div>
                
                <p class="paragraph-185">
                    Before our doctor can authorize your prescription, {{ getUserState }} requires you to submit a short video. 
                    This helps confirm your identity and ensures we're providing the best care.
                </p>
                
                <div class="div-block-2119">
                    <div class="text-block-1514">
                        What to Say in Your Video <span class="text-span-344">(30–60 seconds max):</span>
                    </div>
                    
                    <div class="instructions-bullet">
                        <div class="div-block-2120">
                            <div class="text-block-1518">1.</div>
                        </div>
                        <div class="text-block-1515 bullet">
                            <strong>Start by stating your </strong><strong>full name and date of birth.</strong>
                        </div>
                    </div>
                    
                    <div class="instructions-bullet">
                        <div class="div-block-2120">
                            <div class="text-block-1518">2.</div>
                        </div>
                        <div class="text-block-1515 bullet">
                            <strong>Then briefly </strong><strong>share why you're seeking weight loss medication - </strong>
                            <span class="text-span-345">just a sentence or two is perfect.</span>
                        </div>
                    </div>
                    
                    <div class="text-block-1515 smaller">
                        This doesn't need to be polished or professional—think of it like a voicemail to your doctor. Just be yourself!
                    </div>
                </div>
                
                <div class="text-block-1516">
                    You can <span class="text-span-342">record directly or upload a video</span> from your phone or computer.
                </div>

                <!-- Camera Recording Modal -->
                <div v-if="showCameraModal" class="camera-overlay" @click="closeCameraModal">
                    <div class="camera-simple" @click.stop>
                        <video ref="cameraPreview" autoplay muted playsinline></video>

                        <!-- Countdown -->
                        <div v-if="countdown > 0" class="countdown">{{ countdown }}</div>

                        <!-- Recording indicator -->
                        <div v-if="isRecording" class="rec-indicator">● REC {{ formatTime(recordingTime) }}</div>

                        <!-- Record button (when not recording and no video) -->
                        <div v-if="!isRecording && !recordedVideoBlob" class="record-controls">
                            <button @click="startRecording" :disabled="!cameraReady" class="record-btn">●</button>
                        </div>

                        <!-- Stop button (when recording) -->
                        <div v-if="isRecording" class="stop-controls">
                            <button @click="stopRecording" class="stop-btn">■</button>
                        </div>

                        <!-- Playback controls (after recording) -->
                        <div v-if="recordedVideoBlob" class="playback-controls">
                            <button @click="retakeRecording" class="retake-btn" :disabled="uploading">↻</button>
                            <button @click="useRecording" class="use-video-btn" :disabled="uploading">
                                {{ uploading ? 'Uploading...' : 'Use this video' }}
                            </button>
                        </div>

                        <!-- Upload progress (when uploading) -->
                        <div v-if="uploading && uploadProgress > 0" class="upload-progress">
                            <div class="progress-bar">
                                <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                            </div>
                            <div class="progress-text">{{ uploadProgress }}%</div>
                        </div>

                        <!-- Close button -->
                        <button @click="closeCameraModal" class="close-btn">✕</button>

                        <!-- Error message -->
                        <div v-if="cameraError" class="error-msg">{{ cameraError }}</div>
                    </div>
                </div>

                <!-- Upload Options -->
                <div class="upload-options-div">
                    <div class="upload-option-box _1" @click="$refs.fileInput.click()">
                        <img loading="lazy" src="@/assets/images/icon_gallery.svg" alt="" class="image-606">
                        <div class="text-block-1517" v-if="!selectedFile">Upload from your gallery</div>
                        <div class="text-block-1517" v-if="selectedFile">{{ selectedFile.name }}</div>
                        <div class="file-size-info" v-if="selectedFile">{{ formatFileSize(selectedFile.size) }}</div>
                        <input type="file" ref="fileInput" @change="handleFileChange" 
                               class="hidden-file-input" accept="video/*,.mp4,.mov,.avi,.webm">
                    </div>
                    
                    <!-- Desktop camera -->
                    <div class="upload-option-box _2 desktop-only" @click="openCameraModal">
                        <img loading="lazy" src="@/assets/images/icon_record.svg" alt="" class="image-606">
                        <div class="text-block-1517">Record video now</div>
                    </div>

                    <!-- Mobile camera -->
                    <div class="upload-option-box _2 mobile-only" @click="$refs.captureInput.click()">
                        <img loading="lazy" src="@/assets/images/icon_record.svg" alt="" class="image-606">
                        <div class="text-block-1517" v-if="!selectedFile">Record video now</div>
                        <div class="text-block-1517" v-if="selectedFile">{{ selectedFile.name }}</div>
                        <div class="file-size-mobile" v-if="selectedFile">{{ formatFileSize(selectedFile.size) }}</div>
                        <input type="file" ref="captureInput" @change="handleCaptureChange" 
                               class="hidden-file-input" accept="video/*" capture="user">
                    </div>
                </div>

                <!-- Mobile upload progress bar -->
                <div v-if="uploading" class="mobile-upload-progress mobile-only">
                    <div class="upload-status">Uploading... {{ uploadProgress }}%</div>
                    <div class="progress-bar">
                        <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                    </div>
                </div>

                <!-- Continue Button -->
                <a href="#" class="button-150 w-button" 
                   :disabled="!selectedFile || loading || uploading || isCompressing || hasError" 
                   @click.prevent="confirmUpload">
                    {{ buttonText }} <span class="text-span-343">-&gt;</span>
                </a>

                <!-- Enhanced error display -->
                <div v-if="error" class="error-container">
                    <div class="error-content">
                        <div class="error-title">{{ errorTitle || 'Upload Error' }}</div>
                        <div class="error-message">{{ error }}</div>
                        <div v-if="errorSuggestion" class="error-suggestion">{{ errorSuggestion }}</div>
                        <div v-if="uploadRetries > 0" class="retry-info">Retrying... ({{ uploadRetries }}/{{ maxRetries }})</div>
                    </div>
                </div>

                <!-- Success State -->
                <div class="upload-options-div success" v-if="uploadComplete">
                    <div class="upload-option-box success">
                        <img loading="lazy" src="https://cdn.prod.website-files.com/67beb457c3129d45e62f8efc/67e3c08f0a828cc97cc9e3e3_icon_Check%20green.svg" alt="" class="image-607">
                        <div class="text-block-1517">Video Uploaded!</div>
                    </div>
                </div>

                <!-- Video Preview (if available) -->
                <div v-if="previewUrl && canPlayVideo" class="video-preview-section">
                    <div class="video-info">
                        <div v-if="videoDuration" class="video-duration">Duration: {{ formatDuration(videoDuration) }}</div>
                        <div v-if="wasCompressed" class="compression-info">✓ Video was compressed for faster upload</div>
                    </div>
                </div>

                <!-- Compression Dialog -->
                <div v-if="showCompressionDialog" class="compression-dialog">
                    <div class="dialog-content">
                        <h3>Video File Size</h3>
                        <p>Your video is {{ formatFileSize(originalFileSize) }}. We can compress it to upload faster.</p>
                        <div class="dialog-actions">
                            <button @click="startCompression" class="compress-btn">Compress & Upload</button>
                            <button @click="cancelCompression" class="cancel-btn">Cancel</button>
                        </div>
                    </div>
                </div>

                <!-- Compression Progress -->
                <div v-if="isCompressing" class="compression-progress">
                    <div class="progress-text">Making video smaller... {{ compressionProgress }}%</div>
                    <div class="progress-bar">
                        <div class="progress-fill" :style="{ width: compressionProgress + '%' }"></div>
                    </div>
                </div>

                <!-- Browser Warning -->
                <div v-if="showBrowserWarning" class="browser-warning">
                    <div class="warning-content">
                        <p>Your browser may not support all video features. For the best experience, please use Chrome or Safari.</p>
                        <button @click="dismissBrowserWarning" class="dismiss-btn">Got it</button>
                    </div>
                </div>
            </div>
        </div>
    </section>
</div>
</template>
<script>
class RobustVideoCompressor {
    constructor() {
        this.supportsWebCodecs = 'VideoEncoder' in window;
        this.supportsMediaRecorder = 'MediaRecorder' in window;
        this.supportsCanvas = !!document.createElement('canvas').getContext;
    }

    async compressVideo(file, targetSizeMB = 10, onProgress = null) {
        try {
            // Check what compression methods are available
            if (!this.supportsCanvas) {
                throw new Error('UNSUPPORTED_DEVICE');
            }

            const originalSizeMB = file.size / (1024 * 1024);
            if (originalSizeMB <= targetSizeMB) {
                return file; // No compression needed
            }

            // Use the most compatible method
            if (this.supportsMediaRecorder) {
                return await this.compressWithMediaRecorder(file, targetSizeMB, onProgress);
            } else {
                throw new Error('COMPRESSION_NOT_SUPPORTED');
            }
        } catch (error) {
            console.error('Compression error:', error);
            throw error;
        }
    }

    async compressWithMediaRecorder(file, targetSizeMB, onProgress) {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');
            video.muted = true;
            video.playsInline = true;

            video.onloadedmetadata = async () => {
                try {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Calculate safe output dimensions
                    const {
                        width,
                        height
                    } = this.calculateSafeDimensions(video.videoWidth, video.videoHeight, targetSizeMB, file.size);
                    canvas.width = width;
                    canvas.height = height;

                    // Calculate bitrate
                    const bitrate = this.calculateBitrate(targetSizeMB, video.duration);

                    // Create stream with fallback options
                    const stream = canvas.captureStream(15); // Lower FPS for compatibility

                    // Find supported codec
                    const mimeType = this.getSupportedMimeType();
                    if (!mimeType) {
                        throw new Error('NO_SUPPORTED_CODEC');
                    }

                    const options = {
                        mimeType
                    };
                    if (bitrate > 0) {
                        options.videoBitsPerSecond = bitrate;
                    }

                    const mediaRecorder = new MediaRecorder(stream, options);
                    const chunks = [];

                    mediaRecorder.ondataavailable = (event) => {
                        if (event.data.size > 0) {
                            chunks.push(event.data);
                        }
                    };

                    mediaRecorder.onstop = () => {
                        try {
                            const compressedBlob = new Blob(chunks, {
                                type: mimeType
                            });
                            const extension = mimeType.includes('mp4') ? '.mp4' : '.webm';
                            const compressedFile = new File(
                                [compressedBlob],
                                file.name.replace(/\.[^/.]+$/, extension), {
                                    type: mimeType
                                }
                            );
                            resolve(compressedFile);
                        } catch (error) {
                            reject(error);
                        }
                    };

                    mediaRecorder.onerror = (event) => {
                        console.log(event)
                        reject(new Error('RECORDING_ERROR'));
                    };

                    mediaRecorder.start();

                    // Render video with error handling
                    await this.renderVideoSafely(video, canvas, ctx, onProgress, () => {
                        mediaRecorder.stop();
                        stream.getTracks().forEach(track => track.stop());
                    });

                } catch (error) {
                    reject(error);
                }
            };

            video.onerror = () => reject(new Error('VIDEO_LOAD_ERROR'));
            video.src = URL.createObjectURL(file);
        });
    }

    calculateSafeDimensions(videoWidth, videoHeight, targetSizeMB, originalSizeBytes) {
        const originalSizeMB = originalSizeBytes / (1024 * 1024);
        const compressionRatio = targetSizeMB / originalSizeMB;

        let width = videoWidth;
        let height = videoHeight;

        // Scale down based on compression ratio
        if (compressionRatio < 0.2) {
            width = Math.min(width, 480);
            height = Math.min(height, 320);
        } else if (compressionRatio < 0.4) {
            width = Math.min(width, 640);
            height = Math.min(height, 480);
        } else if (compressionRatio < 0.7) {
            width = Math.min(width, 854);
            height = Math.min(height, 480);
        }

        // Maintain aspect ratio
        const aspectRatio = videoWidth / videoHeight;
        height = Math.round(width / aspectRatio);

        // Ensure even dimensions (required for some codecs)
        width = width % 2 === 0 ? width : width - 1;
        height = height % 2 === 0 ? height : height - 1;

        return {
            width,
            height
        };
    }

    getSupportedMimeType() {
        const types = [
            'video/webm;codecs=vp8',
            'video/webm;codecs=vp9',
            'video/webm',
            'video/mp4;codecs=h264',
            'video/mp4'
        ];

        for (const type of types) {
            if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }

        return 'video/webm'; // Fallback
    }

    calculateBitrate(targetSizeMB, durationSeconds) {
        const targetSizeBytes = targetSizeMB * 1024 * 1024;
        const targetBitrate = (targetSizeBytes * 8) / durationSeconds;
        return Math.max(100000, Math.floor(targetBitrate * 0.6)); // Min 100kbps
    }

    async renderVideoSafely(video, canvas, ctx, onProgress, onComplete) {
        const fps = 15; // Lower FPS for older devices
        const totalFrames = Math.max(1, Math.floor(video.duration * fps));
        let currentFrame = 0;
        let consecutiveErrors = 0;
        const maxErrors = 5;

        const renderFrame = () => {
            if (currentFrame >= totalFrames || consecutiveErrors >= maxErrors) {
                onComplete();
                return;
            }

            try {
                const targetTime = Math.min(currentFrame / fps, video.duration - 0.1);
                video.currentTime = targetTime;

                const onSeeked = () => {
                    video.removeEventListener('seeked', onSeeked);

                    try {
                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                        consecutiveErrors = 0; // Reset error count on success

                        if (onProgress) {
                            const progress = Math.min(100, (currentFrame / totalFrames) * 100);
                            onProgress(progress);
                        }

                        currentFrame++;
                        setTimeout(renderFrame, 100); // Slower for stability

                    } catch (drawError) {
                        consecutiveErrors++;
                        currentFrame++;
                        setTimeout(renderFrame, 100);
                    }
                };

                video.addEventListener('seeked', onSeeked);

                // Timeout fallback
                setTimeout(() => {
                    video.removeEventListener('seeked', onSeeked);
                    consecutiveErrors++;
                    currentFrame++;
                    setTimeout(renderFrame, 100);
                }, 2000);

            } catch (error) {
                consecutiveErrors++;
                currentFrame++;
                setTimeout(renderFrame, 100);
            }
        };

        renderFrame();
    }
}

export default {
    name: 'VideoUploader',

    props: {
    },

    data() {
        return {
            selectedFile: null,
            previewUrl: '',
            videoDuration: null,
            error: '',
            errorTitle: '',
            videoType: 'av_video',
            errorSuggestion: '',
            idOfForm:'',
            loading: false,
            uploading: false,
            uploadComplete: false,
            isDragging: false,
            baseUrl: process.env.VUE_APP_API_ENDPOINT,
            maxFileSizeMB: 100,
            targetSizeMB: 60,
            minDurationSeconds: 5,
            maxDurationSeconds: 60,
            uploadProgress: 0,

            // Compression
            compressor: new RobustVideoCompressor(),
            isCompressing: false,
            compressionProgress: 0,
            showCompressionDialog: false,
            originalFileSize: 0,
            wasCompressed: false,

            // Device compatibility
            showBrowserWarning: false,
            canPlayVideo: true,
            slowConnection: false,

            // Retry mechanism
            uploadRetries: 0,
            maxRetries: 3,

            // Camera recording (for desktop)
            showCameraModal: false,
            cameraStream: null,
            mediaRecorder: null,
            recordedChunks: [],
            recordedVideoBlob: null,
            isRecording: false,
            recordingTime: 0,
            recordingTimer: null,
            cameraReady: false,
            cameraError: '',
            countdown: 0,
            countdownTimer: null,

            // Mobile capture
            capturedVideoPreview: null
        }
    },

    computed: {
        buttonText() {
            if (this.uploading) {
                return this.uploadRetries > 0 ? `Retrying Upload... (${this.uploadRetries}/${this.maxRetries})` : 'Uploading Video...'
            }
            if (this.isCompressing) return 'Making Video Smaller...'
            if (this.error) return 'Try Again'
            if (this.uploadComplete) return 'Video Uploaded Successfully!'
            return 'Upload Video'
        },
        hasError() {
            return !!this.error
        },
        getUserState() {
            let userState = this.$route.query.state;
            if (userState) {
                return userState;
            }
            return localStorage.getItem('userState');
        }
    },

    mounted() {
        this.checkBrowserCompatibility();
        this.checkConnectionSpeed();
    },

    beforeDestroy() {
        if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl)
        }
        this.stopCamera()
    },

    methods: {
        checkBrowserCompatibility() {
            this.idOfForm = this.$route.params.id;
            const hasVideoSupport = !!document.createElement('video').canPlayType;
            const hasFileAPI = !!(window.File && window.FileReader && window.FileList && window.Blob);
            const hasMediaRecorder = 'MediaRecorder' in window;

            if (!hasVideoSupport || !hasFileAPI) {
                this.showBrowserWarning = true;
            }

            if (!hasMediaRecorder) {
                this.canPlayVideo = false;
            }
        },

        checkConnectionSpeed() {
            if ('connection' in navigator) {
                const connection = navigator.connection;
                if (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g') {
                    this.slowConnection = true;
                }
            }
        },

        clearSelectedFile() {
            if (this.previewUrl) {
                URL.revokeObjectURL(this.previewUrl);
            }
            this.selectedFile = null;
            this.previewUrl = '';
            this.videoDuration = null;
            this.error = '';
            this.uploadComplete = false;
            this.wasCompressed = false;

            // Clear file inputs
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
            if (this.$refs.captureInput) {
                this.$refs.captureInput.value = '';
            }
        },

        // Camera methods
        async openCameraModal() {
            this.showCameraModal = true;
            this.cameraError = '';
            this.cameraReady = false;

            try {
                // Check if getUserMedia is supported
                if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                    throw new Error('BROWSER_NOT_SUPPORTED');
                }

                // Detect device type
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

                console.log('Device detection - iOS:', isIOS, 'Mobile:', isMobile);

                let stream;

                if (isIOS) {
                    // iOS Safari specific constraints
                    try {
                        stream = await navigator.mediaDevices.getUserMedia({
                            video: {
                                facingMode: 'user',
                                width: {
                                    max: 1280
                                },
                                height: {
                                    max: 720
                                }
                            },
                            audio: false // Start without audio for iOS
                        });
                    } catch (error) {
                        console.warn('iOS with constraints failed, trying basic:', error);
                        // Ultra basic for iOS
                        stream = await navigator.mediaDevices.getUserMedia({
                            video: true,
                            audio: false
                        });
                    }
                } else if (isMobile) {
                    // Other mobile devices
                    try {
                        stream = await navigator.mediaDevices.getUserMedia({
                            video: {
                                facingMode: 'user'
                            },
                            audio: true
                        });
                    } catch (error) {
                        console.warn('Mobile with audio failed, trying video only:', error);
                        stream = await navigator.mediaDevices.getUserMedia({
                            video: {
                                facingMode: 'user'
                            },
                            audio: false
                        });
                    }
                } else {
                    // Desktop
                    stream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            width: {
                                ideal: 1280,
                                max: 1920
                            },
                            height: {
                                ideal: 720,
                                max: 1080
                            },
                            facingMode: 'user'
                        },
                        audio: true
                    });
                }

                this.cameraStream = stream;

                // Wait for next tick to ensure the modal is rendered
                this.$nextTick(() => {
                    if (this.$refs.cameraPreview) {
                        this.$refs.cameraPreview.srcObject = stream;
                        this.$refs.cameraPreview.playsInline = true; // Important for iOS
                        this.cameraReady = true;
                    }
                });

            } catch (error) {
                console.error('Camera access error:', error);
                this.handleCameraError(error);
            }
        },

        handleCameraError(error) {
            console.error('Camera error details:', error);

            // Log browser info for debugging
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
            const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
            console.log('Error on iOS:', isIOS, 'Safari:', isSafari, 'Error:', error.name, error.message);

            if (error.message === 'BROWSER_NOT_SUPPORTED') {
                this.cameraError = 'Camera not supported in this browser. Please try a different browser.';
            } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                if (isIOS) {
                    this.cameraError = 'Camera permission denied. Please tap "Allow" when Safari asks for camera access.';
                } else {
                    this.cameraError = 'Camera permission denied. Please allow camera access and try again.';
                }
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                this.cameraError = 'No camera found. Please check that your camera is working.';
            } else if (error.name === 'NotSupportedError') {
                if (isIOS) {
                    this.cameraError = 'Camera recording not supported. Please update to the latest iOS version.';
                } else {
                    this.cameraError = 'Camera not supported in this browser.';
                }
            } else if (error.name === 'ConstraintNotSatisfiedError' || error.name === 'OverconstrainedError') {
                this.cameraError = 'Camera settings incompatible. Trying with basic settings...';
                // Auto-retry with even more basic settings
                setTimeout(() => {
                    this.retryWithBasicSettings();
                }, 2000);
            } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                this.cameraError = 'Camera is being used by another app. Please close other camera apps and try again.';
            } else {
                // Generic error - be more helpful
                if (isIOS) {
                    this.cameraError = 'Could not access camera. Please make sure Safari has camera permission in Settings > Safari > Camera.';
                } else {
                    this.cameraError = 'Could not access camera. Please check permissions and try again.';
                }
            }
        },

        async retryWithBasicSettings() {
            console.log('Retrying with ultra-basic camera settings...');
            this.cameraError = '';

            try {
                // Ultra basic - just request video, no constraints at all
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true
                });

                this.cameraStream = stream;

                this.$nextTick(() => {
                    if (this.$refs.cameraPreview) {
                        this.$refs.cameraPreview.srcObject = stream;
                        this.$refs.cameraPreview.playsInline = true;
                        this.cameraReady = true;
                        this.cameraError = ''; // Clear any error
                    }
                });

            } catch (retryError) {
                console.error('Retry failed:', retryError);
                this.cameraError = 'Camera access failed. Please check your browser settings.';
            }
        },

        closeCameraModal() {
            this.showCameraModal = false;
            this.stopCamera();
            this.resetRecording();
        },

        stopCamera() {
            if (this.cameraStream) {
                this.cameraStream.getTracks().forEach(track => track.stop());
                this.cameraStream = null;
            }
            this.cameraReady = false;
        },

        resetRecording() {
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }
            if (this.recordingTimer) {
                clearInterval(this.recordingTimer);
                this.recordingTimer = null;
            }
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer);
                this.countdownTimer = null;
            }
            this.mediaRecorder = null;
            this.recordedChunks = [];
            this.recordedVideoBlob = null;
            this.isRecording = false;
            this.recordingTime = 0;
            this.countdown = 0;
        },

        startRecording() {
            if (!this.cameraStream) return;

            // Start countdown
            this.countdown = 3;
            this.countdownTimer = setInterval(() => {
                this.countdown--;
                if (this.countdown <= 0) {
                    clearInterval(this.countdownTimer);
                    this.countdownTimer = null;
                    this.actuallyStartRecording();
                }
            }, 1000);
        },

        actuallyStartRecording() {
            try {
                // Check MediaRecorder support
                if (!window.MediaRecorder) {
                    throw new Error('Video recording not supported in this browser');
                }

                // iOS Safari has limited MediaRecorder support
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

                // Get supported MIME type with iOS consideration
                let mimeType;
                if (isIOS) {
                    // iOS Safari prefers mp4
                    if (MediaRecorder.isTypeSupported('video/mp4')) {
                        mimeType = 'video/mp4';
                    } else if (MediaRecorder.isTypeSupported('video/webm')) {
                        mimeType = 'video/webm';
                    } else {
                        mimeType = 'video/mp4'; // Fallback
                    }
                } else {
                    mimeType = this.getSupportedMimeType();
                }

                console.log('Using MIME type:', mimeType, 'on iOS:', isIOS);

                // Create MediaRecorder with device-specific options
                const options = {
                    mimeType
                };

                // Adjust bitrate based on device
                if (isIOS) {
                    options.videoBitsPerSecond = 800000; // 800 kbps for iOS
                } else if (/Android/i.test(navigator.userAgent)) {
                    options.videoBitsPerSecond = 1000000; // 1 Mbps for Android
                } else {
                    options.videoBitsPerSecond = 2000000; // 2 Mbps for desktop
                }

                this.mediaRecorder = new MediaRecorder(this.cameraStream, options);
                this.recordedChunks = [];

                this.mediaRecorder.ondataavailable = (event) => {
                    if (event.data.size > 0) {
                        this.recordedChunks.push(event.data);
                        console.log('Chunk received:', event.data.size, 'bytes');
                    }
                };

                this.mediaRecorder.onstop = () => {
                    const blob = new Blob(this.recordedChunks, {
                        type: mimeType
                    });
                    this.recordedVideoBlob = blob;
                    console.log('Recording complete, blob size:', blob.size, 'type:', blob.type);
                };

                this.mediaRecorder.onerror = (event) => {
                    console.error('MediaRecorder error:', event);
                    this.cameraError = 'Recording failed. Please try again.';
                    this.isRecording = false;
                    if (this.recordingTimer) {
                        clearInterval(this.recordingTimer);
                        this.recordingTimer = null;
                    }
                };

                // Start recording - iOS needs different chunk sizes
                const chunkSize = isIOS ? 2000 : 1000; // 2 seconds for iOS, 1 second for others
                this.mediaRecorder.start(chunkSize);
                this.isRecording = true;
                this.recordingTime = 0;

                console.log('Recording started with chunk size:', chunkSize);

                // Start timer
                this.recordingTimer = setInterval(() => {
                    this.recordingTime++;

                    // Auto-stop at max duration
                    if (this.recordingTime >= this.maxDurationSeconds) {
                        this.stopRecording();
                    }
                }, 1000);

            } catch (error) {
                console.error('Recording start error:', error);
                this.cameraError = 'Could not start recording: ' + error.message;
            }
        },

        stopRecording() {
            if (this.mediaRecorder && this.isRecording) {
                this.mediaRecorder.stop();
                this.isRecording = false;

                if (this.recordingTimer) {
                    clearInterval(this.recordingTimer);
                    this.recordingTimer = null;
                }
            }
        },

        getSupportedMimeType() {
            const types = [
                'video/webm;codecs=vp9',
                'video/webm;codecs=vp8',
                'video/webm;codecs=h264',
                'video/webm',
                'video/mp4;codecs=h264',
                'video/mp4'
            ];

            for (const type of types) {
                if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)) {
                    return type;
                }
            }

            // Fallback for older mobile browsers
            return 'video/webm';
        },

        playRecording() {
            if (this.recordedVideoBlob && this.$refs.cameraPreview) {
                const url = URL.createObjectURL(this.recordedVideoBlob);
                this.$refs.cameraPreview.srcObject = null;
                this.$refs.cameraPreview.src = url;
                this.$refs.cameraPreview.play();
            }
        },

        retakeRecording() {
            this.resetRecording();
            // Restart camera stream
            if (this.$refs.cameraPreview && this.cameraStream) {
                this.$refs.cameraPreview.srcObject = this.cameraStream;
                this.$refs.cameraPreview.src = '';
            }
        },

        async useRecording() {
            if (!this.recordedVideoBlob) return;

            try {
                // Convert blob to file
                const file = new File([this.recordedVideoBlob], `recorded_video_${Date.now()}.webm`, {
                    type: this.recordedVideoBlob.type
                });

                // Start upload process
                this.uploading = true;
                this.error = '';
                this.uploadProgress = 0;

                // Generate unique filename
               

                const filename = `${this.generateUniqueFilename(file.name)}`;
                const urlData = await this.getSignedUrl(filename, file.type);

                if (!urlData?.url) {
                    throw new Error('Failed to get upload URL');
                }

                // Upload to S3
                await this.uploadToS3(urlData.url, file);

                // Success
                this.uploadComplete = true;
                this.uploadRetries = 0;

                // Close camera modal
                this.closeCameraModal();

                // Set the file for the main component
                this.selectedFile = file;
                this.videoDuration = this.recordingTime;
                this.createPreviewUrl(file);

                // Emit upload event
                let newFileName = `public/user_med_images/${filename}`
                this.postToLambda(newFileName, this.$route.params.id);

            } catch (err) {
                console.error('Upload error:', err);
                await this.handleUploadError(err);
            } finally {
                this.uploading = false;
                this.uploadProgress = 0;
            }
        },
        async postToLambda(s3FileName, uniqueId) {
            try {
                await this.$axios.post(`https://server.prioritymeds.com/api/upload-av-video`, {
                    s3FileName,
                    uniqueId,
                });
                this.$router.push('/verify-address');
            } catch (error) {
                console.error("Error triggering Lambda:", error.response?.data || error.message);
            }
        },

        // Mobile capture methods
        handleCaptureChange(event) {
            const file = event.target.files[0];
            if (file) {
                // Set selectedFile immediately so filename shows right away
                this.selectedFile = file
                this.loading = true;
                this.error = '';
                this.capturedVideoPreview = null;

                this.getVideoDuration(file).then(duration => {
                    if (duration < this.minDurationSeconds) {
                        throw new Error('TOO_SHORT');
                    }

                    if (duration > this.maxDurationSeconds) {
                        throw new Error('TOO_LONG');
                    }

                    this.videoDuration = duration;
                    this.capturedVideoPreview = URL.createObjectURL(file);
                    this.loading = false;
                }).catch(err => {
                    this.handleProcessingError(err);
                    this.loading = false;
                });
            }
        },

        formatTime(seconds) {
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        },

        dismissBrowserWarning() {
            this.showBrowserWarning = false;
        },

        resetComponent() {
            if (this.previewUrl) {
                URL.revokeObjectURL(this.previewUrl)
            }
            this.selectedFile = null
            this.previewUrl = ''
            this.videoDuration = null
            this.uploadComplete = false
            this.error = ''
            this.errorTitle = ''
            this.errorSuggestion = ''
            this.loading = false
            this.uploading = false
            this.uploadProgress = 0
            this.isCompressing = false
            this.compressionProgress = 0
            this.showCompressionDialog = false
            this.wasCompressed = false
            this.uploadRetries = 0

            // Clear file input
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
        },

        formatDuration(seconds) {
            const mins = Math.floor(seconds / 60)
            const secs = Math.floor(seconds % 60)
            return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs} seconds`
        },

        createPreviewUrl(file) {
            if (this.previewUrl) {
                URL.revokeObjectURL(this.previewUrl)
            }
            try {
                this.previewUrl = URL.createObjectURL(file)
            } catch (error) {
                this.canPlayVideo = false
            }
        },

        async getVideoDuration(file) {
            return new Promise((resolve, reject) => {
                console.log(JSON.stringify(reject))
                const video = document.createElement('video')
                video.preload = 'metadata'
                video.muted = true
                video.playsInline = true

                const cleanup = () => {
                    if (video.src) {
                        URL.revokeObjectURL(video.src)
                    }
                }

                const timeout = setTimeout(() => {
                    cleanup()
                    resolve(8) // Default duration if can't determine
                }, 10000) // 10 second timeout

                video.onloadedmetadata = function () {
                    clearTimeout(timeout)
                    cleanup()
                    resolve(video.duration || 8)
                }

                video.onerror = function () {
                    clearTimeout(timeout)
                    cleanup()
                    resolve(8) // Default duration on error
                }

                video.src = URL.createObjectURL(file)
            })
        },

        handleVideoError() {
            this.canPlayVideo = false
        },

        setError(title, message, suggestion = null) {
            this.errorTitle = title
            this.error = message
            this.errorSuggestion = suggestion
        },

        async processFile(file) {
            this.error = ''
            this.loading = true
            // Don't clear selectedFile here - keep the line below commented out
            // this.selectedFile = null
            this.previewUrl = ''
            this.videoDuration = null
            this.uploadComplete = false
            this.uploading = false
            this.uploadProgress = 0
            this.showCompressionDialog = false
            this.wasCompressed = false

            try {
                if (!file) {
                    throw new Error('NO_FILE')
                }

                if (!file.type.startsWith('video/')) {
                    throw new Error('NOT_VIDEO')
                }

                // Check absolute file size limit
                if (file.size > this.maxFileSizeMB * 1024 * 1024) {
                    throw new Error('TOO_LARGE')
                }

                // Get video duration with timeout
                const duration = await this.getVideoDuration(file)

                // Check duration
                if (duration < this.minDurationSeconds) {
                    throw new Error('TOO_SHORT')
                }

                if (duration > this.maxDurationSeconds) {
                    throw new Error('TOO_LONG')
                }

                // Check if compression is needed
                const fileSizeMB = file.size / (1024 * 1024)
                if (fileSizeMB > this.targetSizeMB) {
                    this.originalFileSize = file.size
                    this.showCompressionDialog = true
                    this.loading = false
                    return
                }

                // File is acceptable - selectedFile is already set in handleFileChange
                this.videoDuration = duration
                this.createPreviewUrl(file)

            } catch (err) {
                this.handleProcessingError(err)
            } finally {
                this.loading = false
            }
        },

        handleProcessingError(err) {
            const errorMessage = typeof err === 'string' ? err : err.message || 'UNKNOWN';

            switch (errorMessage) {
                case 'NO_FILE':
                    this.setError('No File Selected', 'Please choose a video file to upload.')
                    break
                case 'NOT_VIDEO':
                    this.setError('Wrong File Type', 'Please select a video file from your phone or camera.', 'Look for files ending in .mp4, .mov, .webm, or .avi')
                    break
                case 'TOO_LARGE':
                    this.setError('Video File Too Large', `Your video is larger than ${this.maxFileSizeMB}MB.`, 'Try recording a shorter video (30-60 seconds) or use lower quality settings.')
                    break
                case 'TOO_SHORT':
                    this.setError('Video Too Short', `Your video must be at least ${this.minDurationSeconds} seconds long.`, 'Please record a longer video that includes your full name and reason for seeking treatment.')
                    break
                case 'TOO_LONG':
                    this.setError('Video Too Long', `Your video is longer than ${this.maxDurationSeconds} seconds.`, 'Please record a shorter video (30-60 seconds) with just your name and brief reason.')
                    break
                case 'VIDEO_LOAD_ERROR':
                    this.setError('Cannot Read Video', 'Your video file appears to be corrupted or in an unsupported format.', 'Try recording a new video directly in the app.')
                    break
                case 'DURATION_ERROR':
                    this.setError('Video Analysis Failed', 'We could not determine your video length.', 'This sometimes happens with unusual formats. Try recording a new video.')
                    break
                default:
                    // Check if it's a device/browser compatibility issue
                    if (errorMessage.includes('codec') || errorMessage.includes('format')) {
                        this.setError('Video Format Issue', 'This video format is not supported by your browser.', 'Try recording a new video using your phone\'s camera app.')
                    } else if (errorMessage.includes('memory') || errorMessage.includes('allocation')) {
                        this.setError('Device Memory Low', 'Your device doesn\'t have enough memory to process this video.', 'Close other apps and try a shorter video.')
                    } else {
                        this.setError('Video Processing Error', 'We had trouble reading your video file.', 'Try selecting a different video or record a new one using your camera app.')
                    }
            }
        },

        async startCompression() {
            this.showCompressionDialog = false
            this.isCompressing = true
            this.compressionProgress = 0

            try {
                const originalFile = this.$refs.fileInput.files[0]

                const compressedFile = await this.compressor.compressVideo(
                    originalFile,
                    this.targetSizeMB,
                    (progress) => {
                        this.compressionProgress = Math.min(100, progress)
                    }
                )

                const duration = await this.getVideoDuration(compressedFile)

                this.selectedFile = compressedFile
                this.videoDuration = duration
                this.wasCompressed = true
                this.createPreviewUrl(compressedFile)

            } catch (err) {
                console.error('Compression error:', err)

                if (err.message === 'UNSUPPORTED_DEVICE') {
                    this.setError('Device Not Supported', 'Your device cannot compress videos.', 'Try recording a shorter video or use a different device.')
                } else if (err.message === 'COMPRESSION_NOT_SUPPORTED') {
                    this.setError('Compression Not Available', 'Video compression is not supported on your browser.', 'Try using Chrome or Safari, or record a smaller video.')
                } else {
                    this.setError('Compression Failed', 'We could not make your video smaller.', 'Try recording a shorter video or using lower quality camera settings.')
                }
            } finally {
                this.isCompressing = false
                this.compressionProgress = 0
            }
        },

        cancelCompression() {
            this.showCompressionDialog = false
            this.resetComponent()
        },

        handleFileChange(event) {
            const file = event.target.files[0]
            if (file) {
                // Set selectedFile immediately so filename shows right away
                this.selectedFile = file
                this.processFile(file)
            }
        },

        handleDrop(event) {
            this.isDragging = false
            const file = event.dataTransfer.files[0]
            if (file) {
                const dt = new DataTransfer()
                dt.items.add(file)
                this.$refs.fileInput.files = dt.files
                this.processFile(file)
            }
        },

        generateUniqueFilename(originalName) {
            const timestamp = new Date().getTime()
            const extension = originalName.split('.').pop()
            const nameWithoutExt = originalName.split('.').slice(0, -1).join('.')
            return `${this.idOfForm}_${this.videoType}_${timestamp}_${nameWithoutExt}.${extension}`
        },

        async getSignedUrl(filename, contentType) {
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

            try {
                const response = await fetch(`${this.baseUrl}/api/get-signed-url`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        filename,
                        content_type: contentType
                    }),
                    signal: controller.signal
                })

                clearTimeout(timeoutId)

                if (!response.ok) {
                    throw new Error(`Server error: ${response.status}`)
                }

                return await response.json()
            } catch (error) {
                clearTimeout(timeoutId)
                if (error.name === 'AbortError') {
                    throw new Error('Request timeout - please check your internet connection')
                }
                throw new Error('Failed to prepare upload: ' + error.message)
            }
        },

        async uploadToS3(signedUrl, file) {
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                // Set timeout for older devices/slow connections
                xhr.timeout = 300000; // 5 minutes

                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        this.uploadProgress = Math.round((event.loaded / event.total) * 100);
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        this.uploadProgress = 100;

                        // Construct final S3 path for your use
                        const s3FileName = `${file.name}`;

                        resolve(s3FileName); // Resolve with S3 file path
                    } else {
                        reject(new Error(`Upload failed with status: ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error('Network error during upload'));
                });

                xhr.addEventListener('timeout', () => {
                    reject(new Error('Upload timed out - please check your internet connection'));
                });

                xhr.addEventListener('abort', () => {
                    reject(new Error('Upload was cancelled'));
                });

                xhr.open('PUT', signedUrl);
                xhr.setRequestHeader('Content-Type', file.type);
                xhr.send(file);
            });
        },

        async confirmUpload() {
            if (!this.selectedFile || this.uploading || this.isCompressing) return

            this.uploading = true
            this.error = ''
            this.uploadComplete = false
            this.uploadProgress = 0

            try {
                const filename = `${this.generateUniqueFilename(this.selectedFile.name)}`;
                const urlData = await this.getSignedUrl(filename, this.selectedFile.type);


                if (!urlData?.url) {
                    throw new Error('Failed to get upload URL')
                }

                await this.uploadToS3(urlData.url, this.selectedFile)

                this.uploadComplete = true
                this.uploadRetries = 0
                let newFileName = `public/user_med_images/${filename}`
                this.postToLambda(newFileName, this.$route.params.id);
            } catch (err) {
                console.error('Upload error:', err)
                await this.handleUploadError(err)
            } finally {
                this.uploading = false
                this.uploadProgress = 0
            }
        },

        async handleUploadError(err) {
            this.uploadRetries++

            if (this.uploadRetries <= this.maxRetries &&
                (err.message.includes('Network') || err.message.includes('timeout'))) {

                // Auto-retry for network issues
                setTimeout(() => {
                    if (this.uploadRetries <= this.maxRetries) {
                        this.confirmUpload()
                    }
                }, 2000 * this.uploadRetries) // Increasing delay
                return
            }

            // Enhanced error handling with specific scenarios
            if (err.message.includes('timeout') || err.message.includes('timed out')) {
                if (this.selectedFile && this.selectedFile.size > 50 * 1024 * 1024) { // 50MB+
                    this.setError('Large File Upload Failed', 'Your video is too large for your current connection.', 'Try connecting to WiFi, or record a shorter video (under 1 minute).')
                } else {
                    this.setError('Upload Taking Too Long', 'Your internet connection seems slow.', 'Connect to WiFi for faster upload, or try again later.')
                }
            } else if (err.message.includes('Network') || err.message.includes('Failed to fetch') || err.message.includes('ERR_NETWORK')) {
                this.setError('No Internet Connection', 'Cannot reach our servers.', 'Check that you\'re connected to WiFi or mobile data, then try again.')
            } else if (err.message.includes('413') || err.message.includes('too large') || err.message.includes('entity too large')) {
                this.setError('Video File Too Big', 'Your video exceeds the maximum size limit.', 'Record a shorter video (30-60 seconds) or use lower quality camera settings.')
            } else if (err.message.includes('415') || err.message.includes('media type')) {
                this.setError('Video Format Not Supported', 'This video format cannot be uploaded.', 'Try recording a new video directly in the app.')
            } else if (err.message.includes('Server error') || err.message.includes('signed URL') || err.message.includes('500')) {
                this.setError('Server Temporarily Down', 'Our upload system is having issues.', 'Please wait a few minutes and try again.')
            } else if (err.message.includes('401') || err.message.includes('403') || err.message.includes('Unauthorized')) {
                this.setError('Upload Not Authorized', 'Your session may have expired.', 'Please refresh the page and try again.')
            } else if (err.message.includes('quota') || err.message.includes('storage')) {
                this.setError('Storage Limit Reached', 'Our upload storage is temporarily full.', 'Please try again in a few minutes.')
            } else if (err.message.includes('CORS') || err.message.includes('cross-origin')) {
                this.setError('Browser Security Issue', 'Your browser blocked the upload.', 'Try refreshing the page or using a different browser.')
            } else if (err.message.includes('AbortError') || err.message.includes('cancelled')) {
                this.setError('Upload Cancelled', 'The upload was interrupted.', 'This may happen if you switch apps. Please try again.')
            } else {
                // Generic error with file size context
                if (this.selectedFile && this.selectedFile.size > 20 * 1024 * 1024) { // 20MB+
                    this.setError('Upload Failed', 'Large video upload failed.', 'Try recording a shorter video or connecting to WiFi.')
                } else {
                    this.setError('Upload Error', 'Something went wrong uploading your video.', 'Please check your internet connection and try again.')
                }
            }

            this.uploadRetries = 0 // Reset for next attempt
        }
    }
}
</script>

<style scoped>
.text-span-294 {
  background-image: linear-gradient(#fff0 73%, #01d449 73% 88%, #fff0 88%);
}

.button-137 {
  color: #000;
  text-align: center;
  background-color: #e9ff86;
  border-radius: 9px;
  width: 100%;
  padding-top: 20px;
  padding-bottom: 20px;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.button-137:hover {
  background-color: #c9e8ff;
}

.link-block-92 {
  margin-bottom: 10px;
}

.section_logo-bar {
  z-index: 999;
  position: static;
  inset: 0% 0% auto;
}

.div-block-2019 {
  grid-column-gap: 3px;
  grid-row-gap: 3px;
  justify-content: center;
  align-items: flex-start;
  margin-bottom: 5px;
  display: flex;
}

.logonav_div {
  flex-flow: column;
  align-items: center;
  width: 600px;
  padding-top: 30px;
  padding-bottom: 0;
  display: flex;
}

.logonav_wrap {
  background-image: linear-gradient(#fff 80%, #fff0);
  flex-flow: column;
  align-items: center;
  display: flex;
}

.logonav_progressdiv {
  justify-content: space-between;
  align-items: center;
  width: 60%;
  margin-bottom: 30px;
  display: none;
}

.bar-header-text-2 {
  color: #000;
  font-size: 26px;
  font-weight: 800;
  line-height: 26px;
}

.text-block-1364 {
  color: #000;
  font-size: 18px;
  line-height: 24px;
}

.list-8 {
  margin-bottom: 0;
  padding-left: 13px;
}

.text-block-1366 {
  color: #000;
}

.barprice_div {
  flex-flow: column;
  justify-content: center;
  align-items: center;
  width: 22%;
  display: flex;
}

.text-block-1367 {
  color: #000;
  text-align: center;
  margin-bottom: 15px;
  font-size: 28px;
  font-weight: 900;
  line-height: 34px;
}

.text-block-1362 {
  color: #000;
  font-size: 40px;
  font-weight: 700;
  line-height: 30px;
}

.ty_maindiv {
  flex-flow: column;
  align-items: center;
  display: flex;
}

.bar-subheader-text-2 {
  color: #000;
  margin-top: 0;
  margin-bottom: 5px;
  font-size: 15px;
  font-weight: 800;
  line-height: 22px;
}

.bar-header {
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  justify-content: flex-start;
  align-items: flex-end;
  margin-bottom: 5px;
  display: flex;
}

.bar-header.gift {
  margin-bottom: 0;
}

.text-span-295 {
  font-weight: 900;
}

.barimg_div-2 {
  background-color: #52b2cf;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  width: 20%;
  display: flex;
}

.text-block-1363 {
  color: #000;
  font-size: 16px;
  font-weight: 900;
}

.list-item-7 {
  color: #000;
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 15px;
}

.checkoutb_wrap-2 {
  background-color: #fff;
  flex-flow: column;
  align-items: center;
  padding-top: 30px;
  padding-bottom: 130px;
  display: flex;
}

.text-block-1361 {
  color: #000;
  margin-bottom: 30px;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
}

.list-item-15 {
  color: #000;
  font-size: 13px;
  line-height: 15px;
}

.text-block-1360 {
  color: #000;
  text-align: center;
  font-weight: 800;
}

.ty_gift_div {
  margin-bottom: 30px;
}

.text-block-1365 {
  color: #000;
  font-size: 16px;
  font-weight: 900;
}

.text-span-297 {
  font-size: 20px;
  font-weight: 900;
}

.text-span-297.under {
  text-decoration: underline;
}

.barinfo_div {
  flex-flow: column;
  justify-content: center;
  width: 56%;
  padding-left: 10px;
  display: flex;
}

.text-span-296 {
  font-family: Inter, sans-serif;
  font-weight: 400;
}

.barflex {
  justify-content: space-between;
  display: flex;
}

.productbar_div-2 {
  border: 1px solid #4e7882;
  border-radius: 9px;
  width: 100%;
  margin-bottom: 20px;
  padding: 10px;
  position: relative;
}

.productbar_div-2.gift {
  border-width: 3px;
  border-color: #48d1d8;
}

.ty_buttondiv {
  width: 100%;
  margin-top: 50px;
}

.checkout_part-2 {
  width: 480px;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 30px;
}

.bar-body-text-2 {
  color: #000;
  margin-top: 0;
  margin-bottom: 0;
  font-size: 14px;
  line-height: 16px;
}

.ty_no-div {
  width: 100%;
  margin-bottom: 30px;
}

.image-583 {
  height: 40px;
}

.image-582 {
  width: 25px;
  position: absolute;
  inset: -6% -2% auto auto;
}

.progress_step_div {
  background-color: #f3f3f3;
  border-radius: 99px;
  width: 23%;
  height: 6px;
}

.progress_step_div.current {
  background-color: #c9e8ff;
  height: 6px;
}

.upload-options-div {
  grid-column-gap: 20px;
  grid-row-gap: 20px;
  justify-content: center;
  width: 100%;
  margin-bottom: 20px;
  display: flex;
}

.upload-options-div.success {
  margin-top: 20px;
}

.text-block-1518 {
  color: #fff;
  text-align: center;
  font-size: 18px;
  font-weight: 900;
  line-height: 24px;
}

.div-block-2120 {
  flex-flow: column;
  justify-content: center;
  align-items: center;
  padding-left: 5px;
  padding-right: 5px;
  display: flex;
}

.text-span-341 {
  font-size: 19px;
  font-weight: 600;
  line-height: 28px;
}

.instructions-bullet {
  grid-column-gap: 5px;
  grid-row-gap: 5px;
  color: #fff;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
}

.text-block-1515 {
  color: #000;
  text-align: left;
  margin-bottom: 10px;
  font-size: 17px;
  font-weight: 400;
  line-height: 24px;
}

.text-block-1515.bullet, .text-block-1515.smaller {
  color: #fff;
  font-size: 16px;
}

.image-607 {
  width: 25px;
  margin-bottom: 10px;
}

.div-block-2119 {
  color: #000;
  background-color: #2e9bf0;
  border-radius: 11px;
  margin-bottom: 25px;
  padding: 15px 30px;
}

.text-span-342 {
  font-size: 20px;
  font-weight: 800;
}

.text-span-343 {
  font-family: Inter, sans-serif;
}

.text-block-1513 {
  color: #000;
  text-align: center;
  margin-bottom: 20px;
  font-size: 36px;
  font-weight: 700;
  line-height: 34px;
}

.image-606 {
  width: 40px;
  margin-bottom: 10px;
}

.text-block-1516 {
  color: #000;
  text-align: center;
  margin-bottom: 25px;
  font-size: 20px;
  font-weight: 400;
  line-height: 30px;
}

.upload-option-box {
  border: 1px dashed #52b2cf;
  border-radius: 11px;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  width: 35%;
  padding-top: 20px;
  padding-bottom: 20px;
  display: flex;
}

.upload-option-box._1 {
  border-color: #2e9bf0;
  padding-top: 15px;
  padding-bottom: 15px;
}

.upload-option-box.success {
  border-style: solid;
  border-color: #13e300;
}

.upload-option-box._2 {
  border-color: #ff4c46;
}

.paragraph-185 {
  color: #000;
  text-align: center;
  margin-bottom: 30px;
  font-size: 18px;
  line-height: 24px;
}

.text-block-1514 {
  color: #fff;
  text-align: center;
  margin-bottom: 15px;
  font-size: 23px;
  font-weight: 800;
  line-height: 26px;
}

.text-block-1517 {
  color: #000;
  text-align: center;
  font-size: 16px;
  line-height: 24px;
}

.text-span-344 {
  font-size: 24px;
}

.upload-wrap {
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;
  padding-top: 15px;
  padding-bottom: 80px;
  display: flex;
}

.button-150 {
  text-align: center;
  background-color: #01d449;
  border-radius: 6px;
  width: 70%;
  margin-top: 20px;
  padding-top: 15px;
  padding-bottom: 17px;
  font-size: 18px;
  line-height: 24px;
}

.button-150:hover {
  background-color: #2e9bf0;
}

.uploadvid_div {
  color: #000;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;
  width: 900px;
  font-family: Inter, sans-serif;
  display: flex;
}

.code-embed {
  text-align: center;
  max-width: none;
  overflow: hidden;
}

.footer-column-div.categories, .footer-column-div.company {
  display: none;
}

.footer-column-div.legal {
  padding-top: 10px;
}

.div-block-1989 {
  width: 150px;
}

.link {
  color: #fff;
  text-decoration: none;
}

.footer-text-link {
  color: #fff;
  margin-bottom: 10px;
  font-family: Inter, sans-serif;
}

.footer-div-wrap {
  background-color: #2e9bf0;
  padding: 30px 8%;
}

.footer-div2-right {
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  align-items: center;
  display: flex;
}

.footer-header {
  color: #fff;
  margin-bottom: 10px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 900;
  line-height: 22px;
}

.footer-div1-right {
  grid-column-gap: 100px;
  grid-row-gap: 100px;
  display: flex;
}

.text-block-143 {
  color: #fff;
  font-family: Inter, sans-serif;
  font-size: 12px;
}

.footer-logo {
  height: 45px;
  margin-bottom: 10px;
  display: block;
}

.text-block-1220 {
  color: #fff;
  font-family: Inter, sans-serif;
  font-size: 10px;
  line-height: 15px;
}

.footer-div-2 {
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  display: flex;
}

.footer-div-1 {
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  display: flex;
}

@media screen and (max-width: 479px) {
  .link-block-92 {
    margin-bottom: 20px;
  }

  .div-block-2019 {
    margin-bottom: 0;
  }

  .logonav_div {
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    padding-top: 20px;
    padding-left: 3%;
    padding-right: 3%;
  }

  .bar-header-text-2 {
    font-size: 18px;
    line-height: 20px;
  }

  .text-block-1366 {
    font-size: 12px;
    line-height: 14px;
  }

  .barprice_div {
    margin-top: 5px;
  }

  .text-block-1362 {
    font-size: 23px;
    line-height: 27px;
  }

  .bar-subheader-text-2 {
    margin-bottom: 5px;
    font-size: 13px;
    line-height: 14px;
  }

  .bar-header {
    grid-column-gap: 0px;
    grid-row-gap: 0px;
    flex-flow: column;
    justify-content: flex-start;
    align-items: flex-start;
    margin-bottom: 5px;
  }

  .bar-header.gift {
    grid-column-gap: 0px;
    grid-row-gap: 0px;
    flex-flow: column;
    justify-content: flex-start;
    align-items: flex-start;
  }

  .text-block-1363 {
    font-size: 12px;
    line-height: 20px;
  }

  .checkoutb_wrap-2 {
    padding-top: 20px;
    padding-left: 3%;
    padding-right: 3%;
  }

  .text-block-1360 {
    font-size: 11px;
    line-height: 14px;
  }

  .text-block-1365 {
    font-size: 12px;
  }

  .barinfo_div {
    width: 54%;
  }

  .barinfo_div.gift {
    padding-left: 0;
  }

  .barflex {
    align-items: flex-start;
  }

  .checkout_part-2 {
    width: 100%;
    margin-top: 0;
  }

  .bar-body-text-2 {
    font-size: 12px;
    line-height: 16px;
  }

  .image-582 {
    top: -6%;
    right: -3%;
  }

  .upload-options-div {
    flex-flow: column;
  }

  .text-block-1518 {
    font-size: 16px;
    line-height: 20px;
  }

  .instructions-bullet {
    grid-column-gap: 0px;
    grid-row-gap: 0px;
    width: 100%;
  }

  .text-block-1515 {
    text-align: center;
    font-size: 16px;
    line-height: 20px;
  }

  .text-block-1515.bullet {
    text-align: left;
    font-size: 15px;
  }

  .text-block-1515.smaller {
    font-size: 14px;
  }

  .div-block-2119 {
    flex-flow: column;
    justify-content: flex-start;
    align-items: center;
    padding: 15px 10px;
    display: flex;
  }

  .text-span-342 {
    font-size: 18px;
  }

  .text-block-1513 {
    margin-bottom: 10px;
    font-size: 30px;
  }

  .text-block-1516 {
    font-size: 18px;
    line-height: 24px;
  }

  .upload-option-box {
    width: 100%;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  .upload-option-box._2 {
    border-color: #4e7882;
  }

  .paragraph-185 {
    margin-bottom: 20px;
    font-size: 17px;
    line-height: 22px;
  }

  .text-block-1514 {
    width: 80%;
    margin-bottom: 10px;
    font-size: 22px;
    line-height: 22px;
  }

  .text-span-344 {
    font-size: 18px;
    line-height: 22px;
  }

  .upload-wrap {
    padding-top: 0;
    padding-left: 3%;
    padding-right: 3%;
  }

  .button-150 {
    width: 100%;
    margin-top: 5px;
  }

  .uploadvid_div {
    width: 100%;
  }

  .footer-column-div.legal {
    margin-bottom: 20px;
  }

  .footer-div-wrap {
    padding-left: 4%;
    padding-right: 4%;
  }

  .footer-div1-right {
    grid-column-gap: 30px;
    grid-row-gap: 30px;
    flex-flow: column;
  }

  .text-block-143 {
    margin-bottom: 20px;
  }

  .footer-logo {
    margin-bottom: 10px;
  }

  .footer-div1-left {
    margin-bottom: 30px;
  }

  .footer-div-2 {
    flex-flow: column;
    align-items: flex-start;
  }

  .footer-div-1 {
    flex-flow: column;
  }

  .text-span-345 {
    font-size: 14px;
  }
}

.hidden-file-input {
    display: none;
}

/* Mobile/Desktop visibility */
@media (max-width: 768px),
(pointer: coarse) {
    .desktop-only {
        display: none !important;
    }
}

@media (min-width: 769px) and (pointer: fine) {
    .mobile-only {
        display: none !important;
    }
}

/* CHANGE 1: Style for mobile file size display */
.file-size-info {
    color: #666;
    font-size: 12px;
    margin-top: 4px;
    font-weight: 500;
}

.file-size-mobile {
    color: #666;
    font-size: 12px;
    margin-top: 4px;
}

/* CHANGE 3: Mobile upload progress styles */
.mobile-upload-progress {
    margin-top: 16px;
    text-align: center;
}

.upload-status {
    color: #666;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
}

.progress-bar {
    width: 100%;
    height: 6px;
    background: #e9ecef;
    border-radius: 3px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: #4ECDC4;
    transition: width 0.3s ease;
}

/* CHANGE 4: Enhanced error display styles */
.error-container {
    margin-top: 20px;
    padding: 16px;
    background: #fff5f5;
    border: 1px solid #fed7d7;
    border-radius: 8px;
}

.error-content {
    flex: 1;
}

.error-title {
    font-weight: 600;
    color: #c53030;
    margin-bottom: 4px;
    font-size: 16px;
}

.error-message {
    color: #2d3748;
    margin-bottom: 8px;
    line-height: 1.4;
}

.error-suggestion {
    background: #edf2f7;
    padding: 8px 12px;
    border-radius: 6px;
    color: #4a5568;
    font-size: 14px;
    margin-bottom: 8px;
    border-left: 3px solid #4299e1;
}

.retry-info {
    color: #805ad5;
    font-size: 14px;
    font-weight: 500;
}

/* Camera Modal Styles */
.camera-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.camera-simple {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    border-radius: 16px;
    overflow: hidden;
}

.camera-simple video {
    width: 100%;
    height: auto;
    display: block;
}

.countdown {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 60px;
    font-weight: bold;
    color: white;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
}

.rec-indicator {
    position: absolute;
    top: 20px;
    left: 20px;
    background: rgba(255, 0, 0, 0.9);
    color: white;
    padding: 8px 12px;
    border-radius: 20px;
    font-weight: bold;
    font-size: 14px;
}

.record-controls,
.stop-controls {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
}

.record-btn,
.stop-btn {
    width: 70px;
    height: 70px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    color: #ff4444;
    font-size: 30px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.record-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.stop-btn {
    color: #333;
}

.playback-controls {
    position: absolute;
    bottom: 30px;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 30px;
}

.retake-btn {
    width: 50px;
    height: 50px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    color: #666;
    font-size: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.use-video-btn {
    background: #4ECDC4;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.use-video-btn:hover {
    background: #45b7b8;
}

.use-video-btn:disabled {
    background: #999;
    cursor: not-allowed;
}

.retake-btn:hover {
    background: rgba(255, 255, 255, 1);
}

.retake-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.upload-progress {
    position: absolute;
    bottom: 100px;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    max-width: 300px;
}

.upload-progress .progress-bar {
    width: 100%;
    height: 6px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
}

.upload-progress .progress-fill {
    height: 100%;
    background: #4ECDC4;
    transition: width 0.3s ease;
}

.progress-text {
    text-align: center;
    color: white;
    font-size: 14px;
    font-weight: 600;
}

.close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 35px;
    height: 35px;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-btn:hover {
    background: rgba(0, 0, 0, 0.8);
}

.error-msg {
    position: absolute;
    bottom: -60px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 0, 0, 0.9);
    color: white;
    padding: 12px 20px;
    border-radius: 8px;
    text-align: center;
    max-width: 300px;
}

/* Captured Video Preview Styles */
.captured-video-section {
    margin-top: 20px;
    padding: 20px;
    background: #f8f9fa;
    border-radius: 12px;
    border: 2px dashed #e9ecef;
}

.captured-video-container {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.captured-video {
    width: 100%;
    max-width: 400px;
    height: auto;
    border-radius: 8px;
    margin: 0 auto;
    background: #000;
}

.captured-video-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.video-details {
    display: flex;
    gap: 15px;
    color: #666;
    font-size: 14px;
}

.video-duration::before {
    content: "⏱ ";
}

.video-size::before {
    content: "📁 ";
}

.captured-video-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.retake-captured-btn {
    padding: 8px 16px;
    border: 2px solid #ff9800;
    background: white;
    color: #ff9800;
    border-radius: 6px;
    transition: all 0.2s;
}

.use-captured-btn:hover:not(:disabled) {
    background: #45b7b8;
}

.use-captured-btn:disabled {
    background: #999;
    cursor: not-allowed;
}

.upload-progress-bar {
    margin-top: 15px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 10px;
    padding: 10px;
    text-align: center;
}

.upload-progress-bar .progress-fill {
    height: 8px;
    background: #4ECDC4;
    border-radius: 4px;
    transition: width 0.3s ease;
    margin-bottom: 8px;
}

.upload-progress-bar .progress-text {
    color: #333;
    font-weight: 600;
    font-size: 14px;
}

/* Mobile responsive */
@media (max-width: 768px) {
    .captured-video-info {
        flex-direction: column;
        align-items: stretch;
    }

    .captured-video-actions {
        justify-content: center;
    }

    .video-details {
        justify-content: center;
    }

    .error-container {
        padding: 12px;
    }

    .error-title {
        font-size: 15px;
    }

    .error-message {
        font-size: 14px;
    }

    .error-suggestion {
        font-size: 13px;
        padding: 6px 10px;
    }
}
</style>